import { useCallback, useMemo } from 'react';
import type { CSSProperties } from 'react';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger
} from 'design-system';
import { Plus } from 'lucide-react';

import { Z_INDEX } from '@/config/z-index';
import { MENU_OFFSET } from './DashboardTargetMenu.styles';
import type { DashboardTargetMenuProps } from './DashboardTargetMenu.types';

export type {
  TabInfo,
  CreateDashboardResult,
  DashboardTargetMenuProps
} from './DashboardTargetMenu.types';

const NEW_DASHBOARD_KEY = '__new_dashboard__';

const ALIGN_BY_PLACEMENT: Record<
  NonNullable<DashboardTargetMenuProps['placement']>,
  'start' | 'center' | 'end'
> = {
  'bottom-start': 'start',
  'bottom-end': 'end',
  bottom: 'center'
};

export const DashboardTargetMenu = ({
  anchorEl,
  open,
  onClose,
  variant = 'with-tabs',
  excludeCurrentTabId: _excludeCurrentTabId,
  showNewPageOption = true,
  showChevron = true,
  onSelectTab,
  onCreateNewTab,
  onSelectDashboard,
  filterTab: _filterTab,
  emptyMessage = 'No other dashboards',
  disabled = false,
  onCreateDashboard,
  dashboards,
  getTabs,
  currentDashboardName: customCurrentDashboardName,
  onError,
  headerText,
  paperStyle,
  placement = 'bottom-start',
  offset: offsetProp = MENU_OFFSET,
  zIndexWhenBelow = Z_INDEX.panel,
  zIndexWhenAbove = Z_INDEX.panel
}: DashboardTargetMenuProps) => {
  const dashboardNamesList = useMemo(
    () =>
      [...dashboards].toSorted((a, b) => {
        if (variant === 'with-tabs') {
          if (a === customCurrentDashboardName) return -1;
          if (b === customCurrentDashboardName) return 1;
        }
        return a.localeCompare(b);
      }),
    [dashboards, customCurrentDashboardName, variant]
  );

  const handleTabClick = useCallback(
    (dashboardName: string, tabId: string, tabName: string) => {
      if (disabled) return;
      onSelectTab?.(dashboardName, { tabId, tabName });
      onClose();
    },
    [disabled, onSelectTab, onClose]
  );

  const handleNewTabClick = useCallback(
    (dashboardName: string) => {
      if (disabled) return;
      onCreateNewTab?.(dashboardName);
      onClose();
    },
    [disabled, onCreateNewTab, onClose]
  );

  const handleDashboardClick = useCallback(
    (dashboardName: string) => {
      if (disabled || variant !== 'dashboard-only') return;
      onSelectDashboard?.(dashboardName);
      onClose();
    },
    [disabled, variant, onSelectDashboard, onClose]
  );

  const handleCreateNewDashboard = useCallback(async () => {
    if (disabled || !onCreateDashboard) return;

    const result = await onCreateDashboard();
    if (!result) return;

    if (!result.ok) {
      onError?.(
        result.message ||
          (result.reason
            ? `Failed to create dashboard: ${result.reason}`
            : 'Failed to create dashboard')
      );
      return;
    }

    if (!result.dashboardName) {
      onError?.('Failed to create dashboard: missing name.');
      return;
    }

    if (variant === 'with-tabs') {
      if (!onCreateNewTab) {
        onError?.('Failed to create dashboard: missing page handler.');
        return;
      }
      onCreateNewTab(result.dashboardName);
    } else {
      onSelectDashboard?.(result.dashboardName);
    }
    onClose();
  }, [disabled, onCreateDashboard, variant, onCreateNewTab, onSelectDashboard, onClose, onError]);

  const isEmpty = dashboardNamesList.length === 0 && !onCreateDashboard;

  return (
    <DropdownMenu
      open={open}
      onOpenChange={next => {
        if (!next) onClose();
      }}
    >
      <DropdownMenuContent
        align={ALIGN_BY_PLACEMENT[placement]}
        alignOffset={offsetProp[0]}
        anchor={anchorEl}
        className='data-[side=bottom]:z-(--menu-z-below) data-[side=top]:z-(--menu-z-above)'
        side='bottom'
        sideOffset={offsetProp[1]}
        style={
          {
            ...paperStyle,
            '--menu-z-below': zIndexWhenBelow,
            '--menu-z-above': zIndexWhenAbove
          } as CSSProperties
        }
      >
        {headerText && (
          <div className='border-t border-b border-white/10 px-1.5 py-1.5 text-[11px] font-semibold'>
            {headerText}
          </div>
        )}

        {isEmpty ? (
          <DropdownMenuItem disabled>{emptyMessage}</DropdownMenuItem>
        ) : (
          <>
            {dashboardNamesList.map(dashboardName => {
              if (variant !== 'with-tabs') {
                return (
                  <DropdownMenuItem
                    key={dashboardName}
                    disabled={disabled}
                    onClick={() => {
                      handleDashboardClick(dashboardName);
                    }}
                  >
                    {dashboardName}
                  </DropdownMenuItem>
                );
              }

              const tabs = getTabs(dashboardName);
              const rowTabs = tabs ?? (showNewPageOption ? [] : null);

              return (
                <DropdownMenuSub key={dashboardName}>
                  <DropdownMenuSubTrigger
                    disabled={disabled || rowTabs === null}
                    hideIcon={!showChevron}
                  >
                    {dashboardName}
                  </DropdownMenuSubTrigger>
                  {rowTabs !== null && (
                    <DropdownMenuSubContent>
                      {rowTabs.map(({ tabId, tabName }) => (
                        <DropdownMenuItem
                          key={tabId}
                          disabled={disabled}
                          onClick={() => {
                            handleTabClick(dashboardName, tabId, tabName);
                          }}
                        >
                          {tabName}
                        </DropdownMenuItem>
                      ))}
                      {showNewPageOption && (
                        <DropdownMenuItem
                          disabled={disabled}
                          onClick={() => {
                            handleNewTabClick(dashboardName);
                          }}
                        >
                          <Plus aria-hidden='true' />
                          New page
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuSubContent>
                  )}
                </DropdownMenuSub>
              );
            })}

            {onCreateDashboard && (
              <DropdownMenuItem
                key={NEW_DASHBOARD_KEY}
                disabled={disabled}
                onClick={handleCreateNewDashboard}
              >
                <Plus aria-hidden='true' />
                New Dashboard
              </DropdownMenuItem>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
